// @ts-nocheck
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import {
    getProductCollectionRealTimeInventory,
    getProductCollectionRealTimePrices,
    getProductCollectionV2,
} from "@insite/client-framework/Services/ProductServiceV2";
import { ProductInventoryDto, ProductModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import getColor from "@insite/mobius/utilities/getColor";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import ProductAddToListLink from "@insite/content-library/Components/ProductAddToListLink";
import ProductListProductCard from "@store/Overrides/Widgets/ProductList/ProductListProductCard";
import ValveConfiguratorQuoteForm from "@store/Library/Widgets/ProductConfiguration/ValveConfigurator/ValveConfiguratorQuoteForm";
import React, { FC, useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";

const spin = keyframes`
    to {
        transform: rotate(360deg);
    }
`;

const Spinner = styled.span`
    width: ${pxToRem(14)};
    height: ${pxToRem(14)};
    border: 2px solid rgba(0, 0, 0, 0.15);
    border-top-color: currentColor;
    border-radius: 50%;
    display: inline-block;
    animation: ${spin} 0.8s linear infinite;
`;

const LoadingRow = styled.div`
    display: inline-flex;
    align-items: center;
    gap: ${pxToRem(8)};
    color: ${getColor("text.accent")};
    font-size: ${pxToRem(13)};
`;

const AddToListRow = styled.div`
    margin-top: ${pxToRem(8)};
    display: flex;
    justify-content: flex-start;
`;

interface SummaryProductState {
    loading: boolean;
    product?: ProductModel;
    pricing?: ProductPriceDto;
    inventory?: ProductInventoryDto;
}

/**
 * Loads the summary product, its real-time price and inventory (mirroring PLP/PDP), then exposes
 * everything through a ProductContext so the shared Add to cart / price / availability widgets work.
 */
export function useSummaryProduct(partNumber: string): SummaryProductState & {
    qtyOrdered: number;
    unitOfMeasure: string;
    setQtyOrdered: (qty: number) => void;
    setUnitOfMeasure: (uom: string) => void;
} {
    const [state, setState] = useState<SummaryProductState>({ loading: !!partNumber });
    const [qtyOrdered, setQtyOrdered] = useState(1);
    const [unitOfMeasure, setUnitOfMeasure] = useState("");

    useEffect(() => {
        const pn = (partNumber || "").trim();
        if (!pn) {
            setState({ loading: false });
            return;
        }

        let cancelled = false;
        setState({ loading: true });

        (async () => {
            try {
                const products = await getProductCollectionV2({ productNumbers: [pn], pageSize: 1 });
                const product = products.products?.find(
                    p => (p.productNumber || "").trim().toUpperCase() === pn.toUpperCase(),
                );
                if (!product) {
                    if (!cancelled) {
                        setState({ loading: false });
                    }
                    return;
                }

                const info = createFromProduct(product);
                const uom = info.unitOfMeasure || "EA";
                const qty = info.qtyOrdered > 0 ? info.qtyOrdered : 1;

                const [pricingResult, inventoryResult] = await Promise.all([
                    getProductCollectionRealTimePrices({
                        productPriceParameters: [
                            {
                                productId: product.id,
                                unitOfMeasure: uom,
                                qtyOrdered: qty,
                            },
                        ],
                    }),
                    getProductCollectionRealTimeInventory({ productIds: [product.id] }).catch(() => undefined),
                ]);

                if (cancelled) {
                    return;
                }

                const pricing = pricingResult.realTimePricingResults?.find(o => o.productId === product.id);
                const inventory = inventoryResult?.realTimeInventoryResults?.find(o => o.productId === product.id);

                setQtyOrdered(qty);
                setUnitOfMeasure(uom);
                setState({ loading: false, product, pricing, inventory });
            } catch {
                if (!cancelled) {
                    setState({ loading: false });
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [partNumber]);

    return { ...state, qtyOrdered, unitOfMeasure, setQtyOrdered, setUnitOfMeasure };
}

export interface ValveConfiguratorSummaryPurchaseProps {
    partNumber: string;
    modelNumber?: string;
    partsListNumber?: string;
    loadingLabel: string;
    "data-test-selector"?: string;
}

/**
 * Renders the PLP-style purchase controls (availability/lead time, price, quantity, Add to cart) for the
 * configured summary part number when it exists in the catalog and returns a real-time price. Otherwise it
 * falls back to a "Request a quote" form that emails the configuration details to an FLS representative.
 */
const ValveConfiguratorSummaryPurchase: FC<ValveConfiguratorSummaryPurchaseProps> = ({
    partNumber,
    modelNumber,
    partsListNumber,
    loadingLabel,
    ...rest
}) => {
    const { loading, product, pricing, inventory, qtyOrdered, unitOfMeasure, setQtyOrdered, setUnitOfMeasure } =
        useSummaryProduct(partNumber);

    const productContext = useMemo<ProductContextModel | undefined>(() => {
        if (!product) {
            return undefined;
        }
        const productInfo: ProductInfo = {
            ...createFromProduct(product),
            qtyOrdered,
            unitOfMeasure: unitOfMeasure || "EA",
            pricing,
            inventory,
            failedToLoadInventory: inventory ? undefined : true,
        };
        return {
            product,
            productInfo,
            onQtyOrderedChanged: (qty: number) => setQtyOrdered(qty > 0 ? qty : 1),
            onUnitOfMeasureChanged: (uom: string) => setUnitOfMeasure(uom),
        };
    }, [product, pricing, inventory, qtyOrdered, unitOfMeasure, setQtyOrdered, setUnitOfMeasure]);

    if (loading) {
        return (
            <LoadingRow {...rest}>
                <Spinner />
                {loadingLabel}
            </LoadingRow>
        );
    }

    // Render the shared PLP row whenever the configured part resolved to a real catalog product.
    // The PLP price/add-to-cart widgets handle the "Requires Quote" state natively for
    // quote-required products, mirroring the product list/detail pages. Only when the part
    // number is not in the catalog do we fall back to the email-based quote request form.
    if (!productContext) {
        return (
            <div {...rest}>
                <ValveConfiguratorQuoteForm
                    partNumber={partNumber}
                    modelNumber={modelNumber}
                    partsListNumber={partsListNumber}
                />
            </div>
        );
    }

    return (
        <ProductContext.Provider value={productContext}>
            <div {...rest}>
                <ProductListProductCard
                    showBrand={false}
                    showTitle={true}
                    showPartNumbers={true}
                    showAvailability={true}
                    showAttributes={false}
                    showImage={false}
                    showCompare={false}
                    showPrice={true}
                    showAddToList={false}
                    showImageBadges={false}
                    showTextBadges={false}
                />
                <AddToListRow>
                    <ProductAddToListLink />
                </AddToListRow>
            </div>
        </ProductContext.Provider>
    );
};

export default ValveConfiguratorSummaryPurchase;
